/**
 * **P-325 (round 11o).** The Update feed card's `board › group › item` breadcrumb, as links.
 *
 * Built from the sandbox capture `discovery/ui-reference/notifications/11o-*`
 * (`_notes-round11o.json`): monday renders this row with Vibe's own `BreadcrumbsBar` — a
 * `nav > ol` of `<a href>` segments, a 14 px glyph in each, a 14 px chevron between them — and
 * every segment is a real link:
 *
 *   board → `/boards/<b>`                 (lands on the board)
 *   group → `/boards/<b>/groups/<g>`      (lands on the board)
 *   item  → `/boards/<b>/pulses/<i>`      (the board with the item card open on Updates)
 *
 * The item is the `aria-current` segment (primary text colour, weight 500); board and group are
 * secondary. Hover tints the segment `--primary-background-hover-color` with a 4 px radius, the
 * focus ring is Vibe's, a clipped segment gets a tooltip — all of it is the Vibe component's
 * own CSS, which is why the component is used rather than redrawn.
 *
 * The one thing added on top is **in-app navigation**: Vibe's anchors are plain `href`s (and
 * its Enter handler assigns `location.href`), which would reload the whole SPA. With
 * `onNavigate` the host's router takes a plain left click, Enter or Space instead; a modified
 * click (⌘/Ctrl/Shift/middle) is left to the browser, so "open in a new tab" still works.
 */
import { BreadcrumbItem, BreadcrumbsBar, icons } from '@elizmonday/ui';
import type { KeyboardEvent, MouseEvent } from 'react';
import { feedBreadcrumbSegments, type FeedBreadcrumbRow } from './feed-breadcrumb';

export interface FeedBreadcrumbProps {
  row: FeedBreadcrumbRow;
  /** The host router's `navigate`. Without it the segments are ordinary links. */
  onNavigate?: (href: string) => void;
}

const SEGMENT_ICONS = { board: icons.board, group: icons.group, item: icons.item } as const;

function anchorOf(target: EventTarget | null, root: Element): HTMLAnchorElement | null {
  const anchor = target instanceof Element ? target.closest('a[href]') : null;
  return anchor instanceof HTMLAnchorElement && root.contains(anchor) ? anchor : null;
}

export function FeedBreadcrumb({ row, onNavigate }: FeedBreadcrumbProps) {
  const segments = feedBreadcrumbSegments(row);

  const onClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (!onNavigate || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = anchorOf(event.target, event.currentTarget);
    if (!anchor) return;
    event.preventDefault();
    onNavigate(anchor.getAttribute('href') ?? '/');
  };

  /* Capture phase, so Vibe's own `onKeyDown` (a full-page `location.href` assignment) never runs. */
  const onKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onNavigate || (event.key !== 'Enter' && event.key !== ' ')) return;
    const anchor = anchorOf(event.target, event.currentTarget);
    if (!anchor) return;
    event.preventDefault();
    event.stopPropagation();
    onNavigate(anchor.getAttribute('href') ?? '/');
  };

  return (
    <div
      className="ez-inbox__crumb"
      data-testid={`inbox-crumb-${row.id}`}
      onClickCapture={onClickCapture}
      onKeyDownCapture={onKeyDownCapture}
    >
      <BreadcrumbsBar type="navigation">
        {segments.map((segment) => (
          <BreadcrumbItem
            key={segment.kind}
            text={segment.text}
            link={segment.href}
            icon={SEGMENT_ICONS[segment.kind]}
            isCurrent={segment.kind === 'item'}
            data-testid={`inbox-crumb-${segment.kind}-${row.id}`}
          />
        ))}
      </BreadcrumbsBar>
    </div>
  );
}
